import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Card, Button, Segmented, InputNumber, Space, Typography, Tooltip, theme } from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  UndoOutlined,
  CheckCircleOutlined,
  RocketOutlined,
  WarningOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { LAUNCH_BUDGET_DEFAULT } from '../../shared/lib/budget';
import {
  baseSales,
  buildSalesSchedule,
  computeForecastMonths,
  summarizeForecast,
  type ForecastCalculatorInputs,
  type ForecastScenario,
  type ForecastSummary,
  type MonthInput,
  type SalesOverrides,
} from '../../shared/lib/forecast60m';
import { formatMoneyCompact } from '../../shared/lib/formatters';
import { fiscalMonthKey } from '../../shared/lib';
import { readableOn, useAppColors } from '../../shared/theme';
import type { SalesPlanData } from './SalesPlan';

const MONTH_KEYS_SHORT = [
  'idea_calc.forecast_amazon.month_jan', 'idea_calc.forecast_amazon.month_feb',
  'idea_calc.forecast_amazon.month_mar', 'idea_calc.forecast_amazon.month_apr',
  'idea_calc.forecast_amazon.month_may', 'idea_calc.forecast_amazon.month_jun',
  'idea_calc.forecast_amazon.month_jul', 'idea_calc.forecast_amazon.month_aug',
  'idea_calc.forecast_amazon.month_sep', 'idea_calc.forecast_amazon.month_oct',
  'idea_calc.forecast_amazon.month_nov', 'idea_calc.forecast_amazon.month_dec',
];

// Арифметика прогноза живёт в shared/lib/forecast60m.ts (её же считает бэкенд-порт);
// здесь — только состояние таблицы и разметка.
type ForecastType = ForecastScenario;
type MonthData = MonthInput;
type CalculatorData = ForecastCalculatorInputs;

/** Единственный выход компонента наружу; форму задаёт библиотека прогноза. */
export type ForecastSummaryData = ForecastSummary;

interface ForecastAmazonUSProps {
  calculatorData?: CalculatorData;
  salesPlanData?: SalesPlanData;
  launchBudget?: number;
  onSummaryChange?: (summary: ForecastSummaryData) => void;
}

const defaultCalcData: CalculatorData = {
  purchasePrice: 4.5,
  shippingPrice: 0.65,
  storageFee: 0.1,
  sellingPrice: 35.59,
  referralFee: 15,
  fbaFee: 8.36,
  otherFees: 0,
  ppcCost: 0.9,
  conversionRate: 19,
  ppcSalesPercentage: 70,
  returnRate: 4.5,
  resellableReturns: 75,
  monthlySales: 0,
  batchQuantity: 400,
};

const defaultSalesPlan: SalesPlanData = {
  salesGrowth: [
    { year: 2025, annualGrowth: 40, monthlyGrowth: 2.8 },
    { year: 2026, annualGrowth: 35, monthlyGrowth: 2.5 },
    { year: 2027, annualGrowth: 30, monthlyGrowth: 2.2 },
    { year: 2028, annualGrowth: 25, monthlyGrowth: 1.9 },
    { year: 2029, annualGrowth: 20, monthlyGrowth: 1.5 },
  ],
  monthlyGrowth2025: [
    { month: 1, monthName: fiscalMonthKey(1), growth: 0 },
    { month: 2, monthName: fiscalMonthKey(2), growth: 5 },
    { month: 3, monthName: fiscalMonthKey(3), growth: 8 },
    { month: 4, monthName: fiscalMonthKey(4), growth: 3 },
    { month: 5, monthName: fiscalMonthKey(5), growth: 4 },
    { month: 6, monthName: fiscalMonthKey(6), growth: 6 },
    { month: 7, monthName: fiscalMonthKey(7), growth: 2 },
    { month: 8, monthName: fiscalMonthKey(8), growth: 3 },
    { month: 9, monthName: fiscalMonthKey(9), growth: 4 },
    { month: 10, monthName: fiscalMonthKey(10), growth: 3 },
    { month: 11, monthName: fiscalMonthKey(11), growth: 2 },
    { month: 12, monthName: fiscalMonthKey(12), growth: 4 },
  ],
  tacos: [
    { month: 1, tacos: 55 },
    { month: 2, tacos: 45 },
    { month: 3, tacos: 35 },
    { month: 4, tacos: 25 },
    { month: 5, tacos: 20 },
  ],
  targetTacos: 17,
};

function generateMonthLabels(t: (key: string) => string): string[] {
  const start = new Date();
  start.setDate(start.getDate() + 30);
  let mi = start.getMonth();
  let year = start.getFullYear() % 100;
  const months: string[] = [];
  for (let i = 0; i < 60; i++) {
    months.push(`${t(MONTH_KEYS_SHORT[mi])} ${year}`);
    mi++;
    if (mi === 12) { mi = 0; year++; }
  }
  return months;
}

const ForecastAmazonUS: React.FC<ForecastAmazonUSProps> = ({
  calculatorData = defaultCalcData,
  salesPlanData = defaultSalesPlan,
  launchBudget: launchBudgetProp,
  onSummaryChange,
}) => {
  const { token } = theme.useToken();
  const c = useAppColors();
  const { t } = useTranslation();
  const [forecastType, setForecastType] = useState<ForecastType>('realistic');
  const [scrollOffset, setScrollOffset] = useState(0);

  const LAUNCH_BUDGET = launchBudgetProp ?? LAUNCH_BUDGET_DEFAULT;

  const getBaseSales = useCallback(
    (ft: ForecastType): number => baseSales(calculatorData.monthlySales, ft),
    [calculatorData.monthlySales],
  );

  const [manuallyEdited, setManuallyEdited] = useState<boolean[]>(Array(60).fill(false));
  const manuallyEditedRef = useRef(manuallyEdited);
  manuallyEditedRef.current = manuallyEdited;

  // Track original edit: {value, type} so we can scale proportionally on type switch
  const editBaseRef = useRef<{ value: number; type: ForecastType } | null>(null);

  const allMonthLabels = useMemo(() => generateMonthLabels(t), [t]);

  const calculateSalesForMonth = useCallback(
    (monthIndex: number, ft: ForecastType): number => {
      const base = getBaseSales(ft);
      if (monthIndex === 0) return base;

      // First 12 months: growth is relative to base (month 0), not previous month
      if (monthIndex <= 12) {
        const md = salesPlanData.monthlyGrowth2025.find((m) => m.month === monthIndex);
        const rate = md ? md.growth / 100 : 0;
        return Math.round(base * (1 + rate));
      }

      // Months 13+: compound from month 12 value using yearly growth rates
      const md12 = salesPlanData.monthlyGrowth2025.find((m) => m.month === 12);
      const rate12 = md12 ? md12.growth / 100 : 0;
      let sales = base * (1 + rate12);
      for (let i = 13; i <= monthIndex; i++) {
        const cy = 2025 + Math.floor((i - 1) / 12);
        const yd = salesPlanData.salesGrowth.find((s) => s.year === cy);
        const rate = yd ? yd.monthlyGrowth / 100 : 0.02;
        sales *= 1 + rate;
      }
      return Math.round(sales);
    },
    [salesPlanData, getBaseSales],
  );

  const [monthsData, setMonthsData] = useState<MonthData[]>(() =>
    Array.from({ length: 60 }, (_, i) => ({
      sales: calculateSalesForMonth(i, 'realistic'),
      price: calculatorData.sellingPrice,
    })),
  );

  const unitCogs = calculatorData.purchasePrice + calculatorData.shippingPrice;

  // Все 60 месяцев одним проходом — в библиотеке, чтобы бэкенд-порт считал то же самое.
  const precomputed = useMemo(
    () => computeForecastMonths(monthsData, calculatorData, salesPlanData, LAUNCH_BUDGET),
    [monthsData, salesPlanData, calculatorData, LAUNCH_BUDGET],
  );

  const updateMonthData = (monthIndex: number, field: keyof MonthData, value: number) => {
    if (field === 'sales') {
      const newEdited = [...manuallyEditedRef.current];
      newEdited[monthIndex] = true;
      manuallyEditedRef.current = newEdited;
      setManuallyEdited(newEdited);
      if (monthIndex === 0) {
        editBaseRef.current = { value, type: forecastType };
      }
    }
    setMonthsData((prev) => {
      const newData = [...prev];
      newData[monthIndex] = { ...newData[monthIndex], [field]: value };
      if (field === 'sales') {
        const edited = manuallyEditedRef.current;
        const base = monthIndex === 0 ? value : newData[0].sales;
        for (let i = monthIndex + 1; i < newData.length; i++) {
          if (edited[i]) break;
          if (i <= 12) {
            const md = salesPlanData.monthlyGrowth2025.find((m) => m.month === i);
            const rate = md ? md.growth / 100 : 0;
            newData[i] = { ...newData[i], sales: Math.round(base * (1 + rate)) };
          } else {
            const cy = 2025 + Math.floor((i - 1) / 12);
            const yd = salesPlanData.salesGrowth.find((s) => s.year === cy);
            const rate = yd ? yd.monthlyGrowth / 100 : 0.02;
            newData[i] = { ...newData[i], sales: Math.round(newData[i - 1].sales * (1 + rate)) };
          }
        }
      }
      return newData;
    });
  };

  const resetToAutoCalculation = () => {
    const cleared = Array(60).fill(false);
    manuallyEditedRef.current = cleared;
    setManuallyEdited(cleared);
    editBaseRef.current = null;
    setMonthsData((prev) =>
      prev.map((m, i) => ({ ...m, sales: calculateSalesForMonth(i, forecastType) })),
    );
  };

  // Recalc on type switch — scale edited base proportionally, recalculate the rest
  useEffect(() => {
    const edited = manuallyEditedRef.current;
    const eb = editBaseRef.current;
    setMonthsData((prev) => {
      // Month 0: if edited, scale proportionally; else use forecast type default
      let base: number;
      if (edited[0] && eb) {
        base = Math.round(eb.value * getBaseSales(forecastType) / getBaseSales(eb.type));
      } else {
        base = getBaseSales(forecastType);
      }
      // Ручные значения (кроме месяца 0 — он уже масштабирован в base) держатся,
      // остальные пересчитываются от них библиотекой — как и раньше здесь построчно.
      const overrides: SalesOverrides = {};
      for (let i = 1; i < prev.length; i++) {
        if (edited[i]) overrides[i] = prev[i].sales;
      }
      return buildSalesSchedule(salesPlanData, base, overrides).map((sales) => ({
        sales,
        price: calculatorData.sellingPrice,
      }));
    });
  }, [forecastType, calculatorData.sellingPrice, salesPlanData, getBaseSales]);

  useEffect(() => {
    if (!onSummaryChange) return;
    onSummaryChange(
      summarizeForecast(precomputed, {
        launchBudget: LAUNCH_BUDGET,
        sellingPrice: calculatorData.sellingPrice,
        cogsPerUnit: unitCogs,
        monthLabels: allMonthLabels,
        // Шесть подписей на 60 месяцев: неполный первый год (6) + четыре полных (48) +
        // неполный хвост (6). Пятью подписями свёртка накрывала только 54 месяца, а
        // таблица и знаменатель ROI берут все 60.
        yearLabels: [t('idea_calc.forecast_amazon.year_2025_partial'), '2026', '2027', '2028', '2029', '2030'],
      }),
    );
  }, [precomputed, onSummaryChange, unitCogs, calculatorData.sellingPrice, allMonthLabels, LAUNCH_BUDGET, t]);

  // Visible columns slice
  const visibleCount = 12;
  const visibleStart = scrollOffset;
  const visibleEnd = Math.min(scrollOffset + visibleCount, 60);

  // Row definitions for the table
  type RowDef = {
    label: string;
    sublabel?: string;
    render: (mi: number) => React.ReactNode;
    editable?: boolean;
    bgColor?: string;
  };

  // Цвета текста таблицы — читаемые (≥ 4,5) на фоне карточки в обеих темах: сигнальные
  // цвета дизайн-системы как текст не проходят (QA 09.10, WS 15587410), см. readableColor.ts.
  const tone = useMemo(
    () => ({
      positive: readableOn(token.colorSuccess, token.colorBgContainer),
      negative: readableOn(token.colorError, token.colorBgContainer),
      ads: readableOn(token.colorWarning, token.colorBgContainer),
      orderChip: readableOn(token.colorPrimary, token.colorPrimaryBg),
    }),
    [token],
  );
  const signTone = (v: number) => (v >= 0 ? tone.positive : tone.negative);

  const coloredMoney = (v: number) => (
    <span style={{ color: signTone(v), fontWeight: 500 }}>
      {formatMoneyCompact(v)}
    </span>
  );

  const rows: RowDef[] = [
    {
      label: t('idea_calc.forecast_amazon.row_sales'),
      editable: true,
      render: (mi) => {
        if (mi < 10) {
          return (
            <InputNumber
              size="small"
              controls={false}
              min={0}
              step={1}
              value={monthsData[mi]?.sales}
              onChange={(v) => updateMonthData(mi, 'sales', v ?? 0)}
              style={{
                width: 70,
                textAlign: 'center',
                fontWeight: manuallyEdited[mi] ? 600 : 400,
                color: manuallyEdited[mi] ? c.warningText : undefined,
              }}
            />
          );
        }
        return (
          <span style={{ color: token.colorTextSecondary }}>
            <LockOutlined style={{ fontSize: 12, marginRight: 2 }} />
            {monthsData[mi]?.sales}
          </span>
        );
      },
      bgColor: c.infoBg,
    },
    {
      label: t('idea_calc.forecast_amazon.row_price'),
      render: (mi) => <span>${monthsData[mi]?.price.toFixed(2)}</span>,
    },
    {
      label: t('idea_calc.forecast_amazon.row_revenue'),
      sublabel: t('idea_calc.forecast_amazon.row_revenue_sub'),
      render: (mi) => {
        const p = precomputed[mi];
        return (
          <div>
            <div>{formatMoneyCompact(p.revenue)}</div>
            <div style={{ fontSize: 13, color: token.colorTextSecondary, fontStyle: 'italic' }}>
              {'\u03A3'} {formatMoneyCompact(p.cumRevenue)}
            </div>
          </div>
        );
      },
    },
    {
      label: t('idea_calc.forecast_amazon.row_fees'),
      render: (mi) => <span>{formatMoneyCompact(precomputed[mi].allFees)}</span>,
    },
    {
      label: t('idea_calc.forecast_amazon.row_ppc_sales'),
      render: (mi) => <span>{precomputed[mi].ppcSalesPercent.toFixed(0)}%</span>,
    },
    {
      label: t('idea_calc.forecast_amazon.row_ads'),
      render: (mi) => <span style={{ color: tone.ads }}>{formatMoneyCompact(precomputed[mi].adSpend)}</span>,
    },
    {
      label: t('idea_calc.forecast_amazon.row_inventory'),
      render: (mi) => {
        const p = precomputed[mi];
        return (
          <div>
            <div>{p.invCurrent}</div>
            {p.invOrder && (
              <span style={{
                background: token.colorPrimaryBg,
                color: tone.orderChip,
                borderRadius: 4,
                padding: '1px 4px',
                fontSize: 13,
                fontWeight: 600,
              }}>
                +{p.invOrder}
              </span>
            )}
          </div>
        );
      },
    },
    {
      label: t('idea_calc.forecast_amazon.row_profit'),
      sublabel: t('idea_calc.forecast_amazon.row_profit_sub'),
      render: (mi) => coloredMoney(precomputed[mi].monthlyProfit),
    },
    {
      label: t('idea_calc.forecast_amazon.row_profit_no_ads'),
      sublabel: t('idea_calc.forecast_amazon.row_profit_no_ads_sub'),
      // По знаку, как «Прибыль»: строка всегда была зелёной, и убыток без рекламы
      // выглядел прибылью (QA 09.10, WS 15587410).
      render: (mi) => coloredMoney(precomputed[mi].profitWithoutAds),
      bgColor: c.successBg,
    },
    {
      label: t('idea_calc.forecast_amazon.row_pl'),
      sublabel: t('idea_calc.forecast_amazon.row_pl_sub'),
      render: (mi) => {
        const p = precomputed[mi];
        return (
          <div>
            <div>{coloredMoney(p.pl)}</div>
            <div style={{ fontSize: 13, color: token.colorTextSecondary, fontStyle: 'italic' }}>
              {'\u03A3'} {formatMoneyCompact(p.cumPL)}
            </div>
          </div>
        );
      },
    },
    {
      label: t('idea_calc.forecast_amazon.row_cash_flow'),
      sublabel: t('idea_calc.forecast_amazon.row_cash_flow_sub'),
      render: (mi) => {
        const p = precomputed[mi];
        return (
          <div>
            <div>{coloredMoney(p.cfMonthly)}</div>
            <div style={{ fontSize: 13, color: token.colorTextSecondary, fontStyle: 'italic' }}>
              {'\u03A3'} {formatMoneyCompact(p.cfCum)}
            </div>
          </div>
        );
      },
    },
    {
      label: t('idea_calc.forecast_amazon.row_margin'),
      render: (mi) => {
        const m = precomputed[mi].margin;
        return <span style={{ color: signTone(m) }}>{m.toFixed(1)}%</span>;
      },
    },
    {
      label: t('idea_calc.forecast_amazon.row_roi'),
      sublabel: t('idea_calc.forecast_amazon.row_roi_sub'),
      render: (mi) => {
        const p = precomputed[mi];
        return (
          <div>
            <div style={{ color: signTone(p.monthlyROI) }}>{p.monthlyROI.toFixed(1)}%</div>
            <div style={{ fontSize: 13, color: token.colorTextSecondary, fontStyle: 'italic' }}>
              {'\u03A3'} {p.cumROI.toFixed(1)}%
            </div>
          </div>
        );
      },
    },
  ];

  const thStyle: React.CSSProperties = {
    padding: '8px 6px',
    textAlign: 'center',
    fontSize: 14,
    fontWeight: 600,
    borderRight: `1px solid ${c.borderSubtle}`,
    whiteSpace: 'nowrap',
    minWidth: 85,
  };

  const tdStyle: React.CSSProperties = {
    padding: '6px 4px',
    textAlign: 'center',
    fontSize: 14,
    borderRight: `1px solid ${c.borderSubtle}`,
    borderBottom: `1px solid ${c.fillSubtle}`,
  };

  const stickyTd: React.CSSProperties = {
    position: 'sticky',
    left: 0,
    background: c.surface,
    zIndex: 2,
    padding: '6px 12px',
    borderRight: `1px solid ${c.borderSubtle}`,
    borderBottom: `1px solid ${c.fillSubtle}`,
    minWidth: 160,
  };

  return (
    <Card
      title={t('idea_calc.forecast_amazon.title')}
      variant="outlined"
    >
      {/* Forecast type selector + navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Segmented
          size="small"
          value={forecastType}
          onChange={(v) => setForecastType(v as ForecastType)}
          options={[
            { label: <><WarningOutlined /> {t('idea_calc.forecast_amazon.scenario_pessimistic')}</>, value: 'pessimistic' },
            { label: <><CheckCircleOutlined /> {t('idea_calc.forecast_amazon.scenario_realistic')}</>, value: 'realistic' },
            { label: <><RocketOutlined /> {t('idea_calc.forecast_amazon.scenario_optimistic')}</>, value: 'optimistic' },
          ]}
        />
        <Space>
          <Button
            size="small"
            type="link"
            icon={<UndoOutlined />}
            onClick={resetToAutoCalculation}
          >
            {t('idea_calc.forecast_amazon.reset_button')}
          </Button>
          <Button
            size="small"
            icon={<LeftOutlined />}
            disabled={scrollOffset === 0}
            onClick={() => setScrollOffset(Math.max(0, scrollOffset - 12))}
          />
          <Tooltip title={t('idea_calc.forecast_amazon.scroll_tooltip')}>
            <Typography.Text type="secondary" style={{ fontSize: 14, cursor: 'help' }}>
              {scrollOffset + 1}–{visibleEnd} / 60
            </Typography.Text>
          </Tooltip>
          <Button
            size="small"
            icon={<RightOutlined />}
            disabled={visibleEnd >= 60}
            onClick={() => setScrollOffset(Math.min(48, scrollOffset + 12))}
          />
        </Space>
      </div>

      {/* Forecast table */}
      <div style={{ overflowX: 'auto', border: `1px solid ${c.borderSubtle}`, borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: c.fillSubtle }}>
              <th style={{ ...thStyle, textAlign: 'left', position: 'sticky', left: 0, background: c.fillSubtle, zIndex: 3, minWidth: 160 }}>
                {t('idea_calc.forecast_amazon.col_metric')}
              </th>
              {allMonthLabels.slice(visibleStart, visibleEnd).map((label, idx) => (
                <th key={visibleStart + idx} style={thStyle}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <td style={stickyTd}>
                  <div style={{ fontWeight: 500, color: c.textSecondary }}>{row.label}</div>
                  {row.sublabel && (
                    <div style={{ fontSize: 13, color: token.colorTextSecondary }}>{row.sublabel}</div>
                  )}
                </td>
                {Array.from({ length: visibleEnd - visibleStart }, (_, idx) => {
                  const mi = visibleStart + idx;
                  return (
                    <td
                      key={mi}
                      style={{
                        ...tdStyle,
                        background: row.editable && mi < 10
                          ? (manuallyEdited[mi] ? c.warningBg : c.infoBg)
                          : undefined,
                      }}
                    >
                      {row.render(mi)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </Card>
  );
};

export default React.memo(ForecastAmazonUS);
